/**
 * Ceiling Tool - Ceiling Creation by Sketch
 * 
 * Ceilings are created by sketching a closed boundary.
 * Features:
 * - Boundary sketch mode
 * - Ceiling types (Grid, GWB)
 * - Level association
 */

import { v4 as uuidv4 } from 'uuid';
import { Element, Layer } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { collectWallLines, computeRoomBoundary } from '../utils/roomBoundary';
import i18n from '../i18n';

// Reusing FloorGeometryData structure for now as they are geometrically similar
export interface CeilingGeometryData {
    boundary: [number, number][]; // 2D polygon points
    thickness: number; // Total thickness
    layers: Layer[];
    levelId: string;
    heightOffsetFromLevel: number;
}

export interface CeilingElement extends Element {
    category: 'Ceilings';
    geometryData: CeilingGeometryData;
}

export interface CeilingToolOptions {
    ceilingType: string;
    levelId: string;
    offset: number; // mm from level
}

export class CeilingTool extends BaseTool {
    id = 'ceiling';
    name = 'Ceiling';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;
    // Automatic-mode miss warning is shown once per activation so repeated
    // picks in an open space don't spam the toast host.
    private autoMissWarned = false;

    activate(context: Parameters<BaseTool['activate']>[0]): void {
        super.activate(context);
        this.points = [];
        this.currentPoint = null;
        this.autoMissWarned = false;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);

        // Automatic mode (BIM "Automatic Ceiling"): the click point picks
        // the room the enclosing walls form and generates its ceiling.
        if (this.context.bimStore.toolProperties.ceilingMode !== 'Sketch') {
            const walls = collectWallLines(this.context.bimStore.project.elements);
            const detected = computeRoomBoundary(snapped, walls);
            if (detected) {
                const options: CeilingToolOptions = {
                    ceilingType: 'Compound Ceiling: 600x600mm Grid',
                    levelId: 'Level 1',
                    offset: 2600,
                };
                this.context.bimStore.addElement(createCeiling(detected.boundary, options));
                return;
            }
            // Not enclosed — fall back to sketch mode like BIM hints.
            if (!this.autoMissWarned) {
                this.autoMissWarned = true;
                this.notify(i18n.t('tools.notify.noEnclosedRoomBoundaryDetectedAt'), 'warning');
            }
        }

        this.points.push(snapped);

        // Check if closed
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                this.points.pop();
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#ff00ff' // Magenta for ceiling
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);

        const options: CeilingToolOptions = {
            ceilingType: 'Compound Ceiling: 600x600mm Grid',
            levelId: 'Level 1',
            offset: 2600 // Default ceiling height
        };

        const ceiling = createCeiling(boundary, options);
        this.context.bimStore.addElement(ceiling);

        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

export function createCeiling(
    boundary: [number, number][], // Closed polygon of 2D points
    options: CeilingToolOptions
): CeilingElement {
    const layers = getCeilingLayers(options.ceilingType);
    const thicknessM = layers.reduce((sum: number, layer: Layer) => sum + layer.thickness, 0);

    const geometryData: CeilingGeometryData = {
        boundary: boundary,
        thickness: thicknessM,
        layers: layers,
        levelId: options.levelId,
        heightOffsetFromLevel: options.offset / 1000
    };

    const ceiling: CeilingElement = {
        id: uuidv4(),
        category: 'Ceilings',
        type: options.ceilingType,
        parameters: {
            'Level': options.levelId,
            'Height Offset From Level': `${options.offset} mm`,
            'Area': calculatePolygonArea(boundary).toFixed(2) + ' m²',
            'Perimeter': calculatePolygonPerimeter(boundary).toFixed(2) + ' m'
        },
        constraints: [],
        geometryData
    };

    return ceiling;
}

function calculatePolygonArea(points: [number, number][]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}

function calculatePolygonPerimeter(points: [number, number][]): number {
    let perimeter = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const dx = points[j][0] - points[i][0];
        const dy = points[j][1] - points[i][1];
        perimeter += Math.sqrt(dx * dx + dy * dy);
    }
    return perimeter;
}

export const DEFAULT_CEILING_TYPES = [
    'Compound Ceiling: 600x600mm Grid',
    'Compound Ceiling: GWB on Metal Stud',
    'Compound Ceiling: Plain'
];

function getCeilingLayers(type: string): Layer[] {
    switch (type) {
        case 'Compound Ceiling: 600x600mm Grid':
            return [
                { function: 'Finish 1', materialId: 'Acoustic Tile', thickness: 0.02, wraps: false },
                { function: 'Structure', materialId: 'Suspension Grid', thickness: 0.05, wraps: false }
            ];
        case 'Compound Ceiling: GWB on Metal Stud':
            return [
                { function: 'Finish 1', materialId: 'Gypsum', thickness: 0.0125, wraps: false },
                { function: 'Structure', materialId: 'Metal Stud', thickness: 0.04, wraps: false }
            ];
        default:
            return [{ function: 'Finish 1', materialId: 'Generic', thickness: 0.02, wraps: false }];
    }
}
